<script setup lang="ts">
import { computed, onMounted, toRefs } from 'vue';
import useVuelidate from '@vuelidate/core';
import { RRule } from 'rrule';

import { AttendeeDropdownOptionInterface, ExtendedDisciplineInterface } from '@/types';
import { useDateTime } from '@/composables';

interface Props {
  rrule: Partial<RRule>;
  scope: string;
  attendees: AttendeeDropdownOptionInterface[];
  vuelidateKey: string;
  disciplineId: number | undefined;
  eventType: string;
}

const props = defineProps<Props>();
const { rrule, scope, attendees, disciplineId, vuelidateKey, eventType } = toRefs(props);
const showError = computed<boolean>(() => !!v$.value.$errors.length);

// returns an array filled with the id of attendees who do not have a valid treatment service dates
const attendeeTreatmentServiceCheckArray = computed<number[]>(() => {
  const invalidArray = attendees.value.map((attendee) => {
    const attendeeId = attendee.value;
    const validTreatments = attendee.disciplines.filter((discipline) => {
      const matchingTreatments = matchingTreatmentServiceFilter(discipline);
      const validServiceDates = validDateFilter(discipline);

      return matchingTreatments && validServiceDates;
    });

    return !validTreatments.length ? attendeeId : 0;
  });

  return invalidArray.filter((number: number) => number > 0);
});

onMounted(() => {
  v$.value.rrule.options.dtstart.$touch();
  v$.value.rrule.options.until.$touch();
});

const betweenCustom = () => {
  return !attendeeTreatmentServiceCheckArray.value.length;
};

const rules = computed(() => ({
  rrule: {
    options: {
      dtstart: { betweenCustom },
      until: { betweenCustom },
    },
  },
}));

const v$ = useVuelidate(rules, { rrule }, { $scope: scope.value, $registerAs: vuelidateKey.value });

const matchingTreatmentServiceFilter = (discipline: ExtendedDisciplineInterface) =>
  discipline.id === disciplineId.value && discipline?.service?.name === eventType.value;

const validDateFilter = (discipline: ExtendedDisciplineInterface) => {
  const start = rrule.value.options?.dtstart;
  const end = rrule.value.options?.until;

  if (!start || !end) return false;

  return (
    new Date(discipline?.end_at as string).valueOf() >= start.valueOf() &&
    new Date(discipline?.end_at as string).valueOf() >= end.valueOf() &&
    new Date(discipline?.start_at as string).valueOf() <= start.valueOf() &&
    new Date(discipline?.start_at as string).valueOf() <= end.valueOf()
  );
};

const outOfServiceDateAttendeeStrings = computed<string[]>(() => {
  if (!showError.value) return [];
  const attendeeDisciplineDateStrings: string[] = [];
  attendeeTreatmentServiceCheckArray.value.forEach((invalidAttendeeId) => {
    const invalidAttendee = attendees.value.find((attendee) => attendee.value == invalidAttendeeId);
    if (invalidAttendee) {
      const invalidDisciplines = invalidAttendee?.disciplines.filter((discipline) => {
        return matchingTreatmentServiceFilter(discipline) && !validDateFilter(discipline);
      });
      if (invalidDisciplines.length) {
        invalidDisciplines.forEach((invalidDiscipline) => {
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

          const startAt = new Date(invalidDiscipline.start_at);
          const endAt = new Date(invalidDiscipline.end_at);
          // if the service ended more than 6 months ago, it does not need to be listed
          if (endAt <= sixMonthsAgo) return;
          const errorString = `${invalidAttendee.name}, ${invalidDiscipline.name} ${
            invalidDiscipline?.service?.name
          } ${useDateTime().formatIntlDate(startAt.toDateString())} - ${useDateTime().formatIntlDate(
            endAt.toDateString(),
          )}`;

          attendeeDisciplineDateStrings.push(errorString);
        });
      }
    }
  });
  return attendeeDisciplineDateStrings;
});
</script>

<template>
  <div v-if="showError" class="service-date-error-container flex">
    <Message class="w-full" severity="warn" :closable="false">
      <div class="px-1 text-base">
        <span class="font-semibold font-sm text-color-red-dark">
          Event dates fall outside a selected student's service dates
        </span>
        <div class="mt-1">
          <ul class="attendee-error-list list-disc">
            <li v-for="(errorString, key) in outOfServiceDateAttendeeStrings" :key="key">
              <span class="text-xs text-color-orange-dark" v-text="errorString" />
            </li>
          </ul>
        </div>
      </div>
    </Message>
  </div>
</template>

<style scoped lang="scss">
.service-date-error-container {
  margin-left: 2.5rem;
  .attendee-error-list {
    padding-inline-start: 1rem;
  }
}
:deep(.p-message-wrapper) {
  display: flex;

  .p-message-icon {
    align-self: start;
    color: $orangeDark !important;
    font-size: 20px;
    &::before {
      margin-left: 5px;
      align-self: start;
    }
  }
}
</style>
